import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
    Box,
    Paper,
    Tabs,
    Tab,
    Button,
    CircularProgress,
    Alert,
    Container,
    Typography,
    Stack
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useAuth } from 'hooks/useAuth';
import { usePreferences } from 'hooks/usePreferences';
import { useSaveBusinessPreferences } from 'hooks/usePreferences';
import BusinessPreferencesForm from './BusinessPreferencesForm';
import { LocationService } from '../../service/locationService';
import { toast } from 'react-toastify';

/**
 * TenantSettings Page
 * 
 * Allows tenant admins to manage:
 * 1. Business Preferences (tenant-wide defaults for all locations)
 * 2. Locations (view locations and manage per-location overrides)
 * 3. (Future) Branding, Billing, Notifications, etc.
 */

// Validation schema for business preferences
const businessPreferencesSchema = Yup.object().shape({
    preferences: Yup.object().shape({
        phoneNumberFormat: Yup.string().required('Phone format required'),
        phoneNumberCountryCode: Yup.string().required('Country code required'),
        timezone: Yup.string().required('Timezone required'),
        currency: Yup.string().required('Currency required'),
        currencySymbol: Yup.string().required('Currency symbol required'),
        currencyPosition: Yup.string().required('Currency position required'),
        decimalPlaces: Yup.number().required('Decimal places required'),
        thousandsSeparator: Yup.string().required('Thousands separator required'),
        decimalSeparator: Yup.string().required('Decimal separator required'),
        dateFormat: Yup.string().required('Date format required'),
        timeFormat: Yup.string().required('Time format required'),
        postalCodeFormat: Yup.string().required('Postal code format required'),
        invoiceNumberFormat: Yup.string().required('Invoice format required'),
        prescriptionNumberFormat: Yup.string().required('Prescription format required'),
        lowStockQuantityThreshold: Yup.number()
            .typeError('Low stock threshold must be a number')
            .min(0, 'Low stock threshold cannot be negative')
            .required('Low stock threshold required'),
        // Measurement and tax fields omitted for now
    })
});

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`settings-tabpanel-${index}`}
            aria-labelledby={`settings-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

export default function TenantSettings() {
    const { user } = useAuth();
    const { preferences: contextPreferences, loading: preferencesLoading } = usePreferences();
    const { saveBusinessPreferences, saving } = useSaveBusinessPreferences();

    const [tabValue, setTabValue] = useState(0);
    const [locations, setLocations] = useState([]);
    const [locationsLoading, setLocationsLoading] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    // Fetch locations on mount
    useEffect(() => {
        if (user?.tenantId) {
            fetchLocations();
        }
    }, [user?.tenantId]);

    const fetchLocations = async () => {
        try {
            setLocationsLoading(true);
            const response = await LocationService.getLocations();
            if (response && response.data) {
                setLocations(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch locations:', error);
            // Non-blocking error; continue to show preferences
        } finally {
            setLocationsLoading(false);
        }
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleSubmit = async (values) => {

        // make sure the deletedInvoiceRetentionDays is not negative, the highest value check will be handled on the backend and return an error if it's above the max allowed
        if (values.preferences.deletedInvoiceRetentionDays < 0) {
            setSubmitError('Deleted invoice retention days must be a non-negative number');
            return;
        }

        if (values.preferences.lowStockQuantityThreshold < 0) {
            setSubmitError('Low stock threshold cannot be negative');
            return;
        }

        try {
            setSubmitError(null);
            await saveBusinessPreferences(values.preferences);
            toast.success('Business preferences saved successfully');
        } catch (error) {
            console.error('Failed to save preferences:', error);
            setSubmitError('Failed to save preferences. Please try again.');
        }
    };

    if (preferencesLoading) {
        return (
            <Container maxWidth="lg" sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: 600 }}>
                    Tenant Settings
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Manage business preferences and locations for {user?.tenant?.name || 'your business'}
                </Typography>
            </Box>

            <Paper elevation={1}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label="settings tabs"
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab label="Business Preferences" id="settings-tab-0" aria-controls="settings-tabpanel-0" />
                    {/* <Tab label="Locations" id="settings-tab-1" aria-controls="settings-tabpanel-1" /> */}
                </Tabs>

                <Box sx={{ p: 3 }}>
                    {/* Business Preferences Tab */}
                    <TabPanel value={tabValue} index={0}>
                        <Formik
                            initialValues={{
                                preferences: contextPreferences || {}
                            }}
                            validationSchema={businessPreferencesSchema}
                            onSubmit={handleSubmit}
                            enableReinitialize
                        >
                            {({ values, errors, touched, isSubmitting }) => (
                                <Form>
                                    {submitError && (
                                        <Alert severity="error" sx={{ mb: 3 }}>
                                            {submitError}
                                        </Alert>
                                    )}

                                    <BusinessPreferencesForm values={values} />

                                    <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            startIcon={<SaveIcon />}
                                            disabled={saving || isSubmitting}
                                        >
                                            {saving || isSubmitting ? 'Saving...' : 'Save Changes'}
                                        </Button>
                                    </Stack>
                                </Form>
                            )}
                        </Formik>
                    </TabPanel>

                    {/* Locations Tab */}
                    {/* <TabPanel value={tabValue} index={1}>
                        <Box>
                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                                Your Locations
                            </Typography>

                            {locationsLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress size={40} />
                                </Box>
                            ) : locations.length === 0 ? (
                                <Alert severity="info">
                                    No locations found. Create your first location to get started.
                                </Alert>
                            ) : (
                                <Stack spacing={2}>
                                    {locations.map((location) => (
                                        <Paper
                                            key={location.id}
                                            sx={{
                                                p: 2,
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                border: '1px solid',
                                                borderColor: 'divider'
                                            }}
                                        >
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                    {location.name}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {location.addressLine1}, {location.city}
                                                </Typography>
                                            </Box>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => {
                                                    // TODO: Navigate to location preferences edit
                                                    toast.info('Location preferences editing coming soon');
                                                }}
                                            >
                                                Edit Preferences
                                            </Button>
                                        </Paper>
                                    ))}
                                </Stack>
                            )}

                            <Box sx={{ mt: 3 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        // TODO: Navigate to create/manage locations
                                        toast.info('Location management coming soon');
                                    }}
                                >
                                    Manage Locations
                                </Button>
                            </Box>
                        </Box>
                    </TabPanel> */}
                </Box>
            </Paper>
        </Container>
    );
}
